/**
 * ImportantInboxCard.jsx · v2.7.45+E13 (Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Red-bordered dashboard widget showing the 5 most recent un-
 * acknowledged Important email escalations. One-click ACK clears the
 * row from view and broadcasts dismissal to every open session via the
 * existing WS pipeline. Polls `/admin/email/important` every 30s and
 * subscribes to `/api/ws/dispatch` for `email.important` push events
 * (instant updates between polls).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Check, Loader2, ExternalLink, AlertOctagon, ChevronRight, ShieldAlert,
} from "lucide-react";

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export default function ImportantInboxCard() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState({});
  const wsRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/email/important?limit=5");
      setAlerts(r.data?.items || []);
    } catch (e) {
      // silent — widget should never crash the dashboard
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    // WebSocket subscription for instant push events between polls
    try {
      const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/i, "ws");
      const token = localStorage.getItem("rtg_admin_token");
      if (wsBase && token) {
        const ws = new WebSocket(`${wsBase}/api/ws/dispatch?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (m?.type === "email.important" || m?.type === "email.important.ack") {
              reload();
            }
          } catch { /* ignore parse errors */ }
        };
        ws.onerror = () => { /* keep widget alive even if WS fails */ };
      }
    } catch { /* WS optional — polling covers it */ }
    return () => {
      clearInterval(t);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [reload]);

  const acknowledge = useCallback(async (alert) => {
    setAcking((a) => ({ ...a, [alert.id]: true }));
    try {
      await adminApi.post(`/admin/email/important/${alert.id}/acknowledge`);
      toast.success(`Acknowledged: ${alert.subject?.slice(0, 60) || "(no subject)"}`);
      setAlerts((cur) => cur.filter((x) => x.id !== alert.id));
    } catch (e) {
      toast.error(`ACK failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setAcking((a) => ({ ...a, [alert.id]: false }));
    }
  }, []);

  return (
    <div
      className="rounded-lg border-2 border-red-300 dark:border-red-900/60 bg-red-50/40 dark:bg-red-950/20 p-4"
      data-testid="important-inbox-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400"/>
          </div>
          <div className="min-w-0">
            <div className="font-bold text-sm text-red-900 dark:text-red-100">Important Mail · Supervisor Triage</div>
            <div className="text-[11px] text-red-700/80 dark:text-red-300/70">
              Live escalations awaiting acknowledgement
            </div>
          </div>
        </div>
        <Link
          to="/admin/email"
          className="text-[11px] uppercase tracking-widest font-bold text-red-700 dark:text-red-300 hover:underline flex items-center gap-1"
          data-testid="important-inbox-open-email"
        >
          Open Inbox <ExternalLink className="h-3 w-3"/>
        </Link>
      </div>

      {loading && (
        <div className="text-xs text-red-700/70 dark:text-red-300/70 flex items-center gap-2 py-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin"/> Loading escalations…
        </div>
      )}

      {!loading && alerts.length === 0 && (
        <div className="text-center py-6">
          <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400 mx-auto mb-1"/>
          <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300">All clear</div>
          <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">
            No un-acknowledged Important escalations.
          </div>
        </div>
      )}

      {!loading && alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div
              key={a.id}
              className="bg-white dark:bg-zinc-950 border border-red-200 dark:border-red-900/40 rounded p-2 flex items-start gap-2"
              data-testid={`important-row-${a.id}`}
            >
              <Zap className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"/>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold truncate text-slate-900 dark:text-zinc-50" title={a.subject}>
                  {a.subject || "(no subject)"}
                </div>
                <div className="text-[10px] text-slate-600 dark:text-zinc-400 truncate">
                  From <b>{(a.from || "—").replace(/<.*?>/g, "").trim()}</b> · to <b>{a.account_email || "—"}</b>
                </div>
                <div className="text-[10px] text-slate-500 dark:text-zinc-500 flex items-center gap-1 mt-0.5">
                  <AlertOctagon className="h-2.5 w-2.5"/>
                  flagged by <b>{a.flagged_by_email || a.flagged_by_id}</b> · {fmtRelative(a.flagged_at)}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => acknowledge(a)}
                disabled={!!acking[a.id]}
                className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500 text-white gap-1 flex-shrink-0"
                data-testid={`important-ack-${a.id}`}
              >
                {acking[a.id] ? <Loader2 className="h-3 w-3 animate-spin"/> : <Check className="h-3 w-3"/>}
                ACK
              </Button>
            </div>
          ))}
          <Link
            to="/admin/email"
            className="text-[11px] uppercase tracking-widest font-bold text-red-700 dark:text-red-300 hover:underline flex items-center justify-end gap-1 pt-1"
            data-testid="important-inbox-see-all"
          >
            See all in inbox <ChevronRight className="h-3 w-3"/>
          </Link>
        </div>
      )}
    </div>
  );
}
